import React, { useState, useEffect } from 'react';
import Dialog from '../../../../common/element/Dialog';
import Upload from '../../../../common/element/Upload';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import { fetchRoleListing } from "../../../../redux/auth/authSlice";
import { selectRoleListingData } from "../../../../redux/auth/authSelector";
import { useDispatch, useSelector } from 'react-redux';
import SweetAlertService from '../../../../common/SweetAlert/SweetAlert';

import {
  addSupportedDocument,
  clearAddSupportedDocument,
  updateSupportedDocument,
  clearUpdateSupportedDocument,
} from '../../../../redux/SupportedDocuments/supportedDocumentsSlice';

import {
  selectAddSupportedDocumentData,
  selectAddSupportedDocumentLoading,
  selectAddSupportedDocumentError,
  selectUpdateSupportedDocumentData,
  selectUpdateSupportedDocumentLoading,
  selectUpdateSupportedDocumentError,
} from '../../../../redux/SupportedDocuments/supportedDocumentsSelectors';

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
const SOS_GROUP_ID = 4; // Science of Self
const SALES_TRAINING_GROUP_ID = 6; // Sales Training Companies

const AddEditSupportedDocument = ({
  dialogOpen,
  onDialogClose,
  documentData,
  mode = 'add',
  onAdded
}) => {
  const dispatch = useDispatch();
  const roles = useSelector(selectRoleListingData);

  const [buttonDisabled, setButtonDisabled] = useState(false);
  
  // New State for Radio selection
  const [accessCategory, setAccessCategory] = useState('session'); // 'session' | 'sos' | 'stc'

  const [formData, setFormData] = useState({
    name: '',
    access_group_ids: [],
    documents: []
  });

  const [formError, setFormErrors] = useState({
    name: '',
    access_group_ids: '',
    documents: ''
  });

  useEffect(() => {
    if (documentData && (mode === 'edit' || mode === 'view')) {
      const name = documentData.name || documentData.documentName || '';

      // Handle access_for array structure from API
      let access_group_ids = [];
      if (Array.isArray(documentData.access_for)) {
        access_group_ids = documentData.access_for.map(a => a.access_group_id);
      } else if (Array.isArray(documentData.accessTypes)) {
        access_group_ids = documentData.accessTypes.map(a => a.access_group_id || a.role_id);
      } else if (Array.isArray(documentData.access_group_ids)) {
        access_group_ids = documentData.access_group_ids;
      } else if (Array.isArray(documentData.access_group_id)) {
        access_group_ids = documentData.access_group_id;
      } else if (Array.isArray(documentData.accessIds)) {
        access_group_ids = documentData.accessIds;
      } else if (Array.isArray(documentData.accessFor)) {
        access_group_ids = documentData.accessFor;
      } else {
        access_group_ids = [];
      }

      // Check if Science of Self (ID 4) or Sales Training (ID 6) is selected
      if (access_group_ids.includes(SOS_GROUP_ID)) {
        setAccessCategory('sos');
        setFormData({ name, access_group_ids: [SOS_GROUP_ID], documents: [] });
      } else if (access_group_ids.includes(SALES_TRAINING_GROUP_ID)) {
        setAccessCategory('stc');
        setFormData({ name, access_group_ids: [SALES_TRAINING_GROUP_ID], documents: [] });
      } else {
        setAccessCategory('session');
        
        // Logic to handle "All" checkbox visual state for Session roles
        const allOption = roles.find(r => r.access_group_name?.toLowerCase() === "all");
        if (allOption && access_group_ids.includes(allOption.access_group_id)) {
          // If "All" is selected in API, expand to show all *visible* checkboxes (excluding ID 4)
          const visibleGroupIds = roles
            .filter(r => r.access_group_id !== SOS_GROUP_ID)
            .map(r => r.access_group_id);
          
          access_group_ids = [...visibleGroupIds];
        }
      }

      // Process Documents
      const documents = Array.isArray(documentData.documents)
        ? documentData.documents.map(d => ({
          uid: d.document_id || Math.random().toString(36).slice(2),
          name: (d.document_url && d.document_url.split('/').pop()) || d.name || `document_${d.document_id}`,
          size: d.document_size || 0,
          url: d.document_url || '',
          _raw: d,
        }))
        : (documentData.documents || []);
        
      setFormData(prev => ({ ...prev, name, access_group_ids, documents }));

    } else {
      setFormData({ name: '', access_group_ids: [], documents: [] });
      setAccessCategory('session');
    }
  }, [documentData, mode, roles]);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const handleInputChange = (field, value) => {
    if (isViewMode) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formError[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleCategoryChange = (category) => {
    if (isViewMode) return;
    setAccessCategory(category);

    if (category === 'sos') {
      setFormData(prev => ({ ...prev, access_group_ids: [SOS_GROUP_ID] }));
    } else if (category === 'stc') {
      setFormData(prev => ({ ...prev, access_group_ids: [SALES_TRAINING_GROUP_ID] }));
    } else {
      setFormData(prev => ({ ...prev, access_group_ids: [] }));
    }
    if (formError.access_group_ids) setFormErrors(prev => ({ ...prev, access_group_ids: '' }));
  };

  // ✅ Checkbox Change (Only for Session mode)
  const handleAccessGroupChange = (value) => {
    if (isViewMode) return;

    // Get roles that are NOT Science of Self (ID 4) and NOT Sales Training (ID 6)
    const visibleRoles = roles.filter(r => r.access_group_id !== SOS_GROUP_ID && r.access_group_id !== SALES_TRAINING_GROUP_ID);
    const visibleGroupIds = visibleRoles.map(r => r.access_group_id);

    let newIds = [...formData.access_group_ids];
    const allOption = roles.find(r => r.access_group_name?.toLowerCase() === "all");

    if (value === allOption?.access_group_id) {
      // Toggle "All" - checks/unchecks only visible roles (excludes SOS ID 4)
      const isAllSelected = newIds.includes(allOption.access_group_id);
      newIds = isAllSelected ? [] : [...visibleGroupIds];
    } else {
      // Individual checkbox clicked
      if (newIds.includes(value)) {
        // Unchecking
        newIds = newIds.filter(id => id !== value);
        if (allOption) newIds = newIds.filter(id => id !== allOption.access_group_id);
      } else {
        // Checking
        newIds.push(value);

        // Logic to auto-check "All" if all visible individuals are checked
        if (allOption) {
          const individualRoles = visibleGroupIds.filter(id => id !== allOption.access_group_id);
          const hasAllIndividuals = individualRoles.every(id => newIds.includes(id));
          
          if (hasAllIndividuals && !newIds.includes(allOption.access_group_id)) {
            newIds.push(allOption.access_group_id);
          }
        }
      }
    }

    setFormData(prev => ({ ...prev, access_group_ids: newIds }));
    if (formError.access_group_ids) setFormErrors(prev => ({ ...prev, access_group_ids: '' }));
  };

  const handleFileChange = (files) => {
    if (isViewMode) return;
    const invalidFiles = files.filter(file => {
      const ext = file.name?.split('.').pop()?.toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext);
    });
    if (invalidFiles.length > 0) {
      SweetAlertService.showError({
        title: 'Invalid File Type!',
        message: 'You can only upload PDF, DOC, DOCX, JPG, or PNG files.',
      });
      return;
    }
    setFormData(prev => ({ ...prev, documents: files }));
    setFormErrors(prevErr => ({ ...prevErr, documents: '' }));
  };

  const handleFileRemove = (files) => {
    if (isViewMode) return;
    setFormData(prev => ({ ...prev, documents: files }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Document name is required';
    if (formData.access_group_ids.length === 0) errors.access_group_ids = 'Please select at least one access option';
    if (formData.documents.length === 0) errors.documents = 'Please upload at least one document';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getPayloadAccessGroupIds = () => {
    const allOption = roles.find(r => r.access_group_name?.toLowerCase() === "all");
    
    // If "All" is selected in Session mode, send only "All" ID
    if (accessCategory === 'session' && allOption && formData.access_group_ids.includes(allOption.access_group_id)) {
      return [allOption.access_group_id];
    }
    
    // Otherwise send selected IDs
    return formData.access_group_ids;
  };

  const handleSubmit = async () => {
    if (isViewMode) return;
    if (!validateForm()) return;
    setButtonDisabled(true);

    const hasFiles = Array.isArray(formData.documents) && formData.documents.length > 0;
    const docId = documentData?.supported_doc_id || documentData?.id;
    
    const payloadAccessGroupIds = getPayloadAccessGroupIds();

    if (isEditMode) {
      const fd = new FormData();
      fd.append('name', formData.name || '');
      fd.append('access_group_ids', JSON.stringify(payloadAccessGroupIds));

      const urlToFile = async (url, filename) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const name = filename || (url.split('/').pop() || 'file');
          return new File([blob], name, { type: blob.type });
        } catch {
          return null;
        }
      };

      const existingPromises = formData.documents.map(async (item) => {
        const isNewFile = item?.originFileObj || (item instanceof File);
        if (!isNewFile) {
          const url = item?.url || item?._raw?.document_url;
          if (url) {
            const filename = item?.name || (url.split('/').pop());
            return await urlToFile(url, filename);
          }
        }
        return null;
      });

      const existingFiles = await Promise.all(existingPromises);
      existingFiles.forEach(f => f && fd.append('documents', f, f.name));
      formData.documents.forEach((file, idx) => {
        const fileObj = file?.originFileObj || (file instanceof File ? file : null);
        if (fileObj) fd.append('documents', fileObj, fileObj.name || `file_${idx}`);
      });

      dispatch(updateSupportedDocument({ id: docId, formData: fd }));
    } else {
      if (hasFiles) {
        const fd = new FormData();
        fd.append('name', formData.name || '');
        fd.append('access_group_ids', JSON.stringify(payloadAccessGroupIds));
        formData.documents.forEach((file, idx) => {
          const fileObj = file?.originFileObj || file;
          if (fileObj) fd.append('documents', fileObj, fileObj.name || `file_${idx}`);
        });
        dispatch(addSupportedDocument(fd));
      } else {
        const payload = {
          name: formData.name,
          access_group_ids: payloadAccessGroupIds,
          documents: formData.documents,
        };
        dispatch(addSupportedDocument(payload));
      }
    }
  };

  const addDocData = useSelector(selectAddSupportedDocumentData);
  const addDocError = useSelector(selectAddSupportedDocumentError);
  const updateDocData = useSelector(selectUpdateSupportedDocumentData);
  const updateDocError = useSelector(selectUpdateSupportedDocumentError);

  useEffect(() => {
    if (addDocData) {
      SweetAlertService.showSuccess({
        title: 'Success!',
        message: 'Supported document has been added successfully.',
        onConfirm: () => {
          dispatch(clearAddSupportedDocument());
          if (typeof onAdded === 'function') onAdded();
          setButtonDisabled(false);
          onDialogClose();
        }
      });
    }
  }, [addDocData]);

  useEffect(() => {
    if (updateDocData) {
      onDialogClose?.();
      setTimeout(() => {
        SweetAlertService.showSuccess({
          title: 'Success!',
          message: 'Supported document has been updated successfully.',
          onConfirm: () => {
            dispatch(clearUpdateSupportedDocument());
            if (typeof onAdded === 'function') onAdded();
            setButtonDisabled(false);
          }
        });
      }, 200);
    }
  }, [updateDocData]);

  useEffect(() => {
    if (updateDocError) {
      SweetAlertService.showError({
        title: 'Error!',
        message: updateDocError || 'Failed to update supported document.',
        onConfirm: () => {
          dispatch(clearUpdateSupportedDocument());
          setButtonDisabled(false);
        }
      });
    }
  }, [updateDocError]);

  useEffect(() => {
    if (addDocError) {
      SweetAlertService.showError({
        title: 'Error!',
        message: addDocError || 'Failed to add supported document.',
        onConfirm: () => {
          dispatch(clearAddSupportedDocument());
          setButtonDisabled(false);
        }
      });
    }
  }, [addDocError]);

  const getTitle = () => {
    if (isViewMode) return 'View Supported Document';
    if (isEditMode) return 'Edit Supported Documents';
    return 'Add Supported Documents';
  };

  useEffect(() => {
    dispatch(fetchRoleListing());
  }, [dispatch]);

  useEffect(() => {
    if (!dialogOpen) {
      setFormData({ name: '', access_group_ids: [], documents: [] });
      setFormErrors({ name: '', access_group_ids: '', documents: '' });
      setAccessCategory('session');
      setButtonDisabled(false);
    }
  }, [dialogOpen]);

  // Determine roles to show in checkboxes (Exclude SOS ID 4 and Sales Training ID 6)
  const visibleRoles = roles ? roles.filter(group => group.access_group_id !== SOS_GROUP_ID && group.access_group_id !== SALES_TRAINING_GROUP_ID) : [];

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={onDialogClose}
      width={800}
      onRequestClose={onDialogClose}
      bodyOpenClassName="overflow-hidden"
    >
      {/* Header */}
      <div className="flex border-b pb-4 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-3">
             <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 7.22222C1 4.28904 1 2.82245 1.91122 1.91122C2.82245 1 4.28904 1 7.22222 1H8.77778C11.7109 1 13.1776 1 14.0888 1.91122C15 2.82245 15 4.28904 15 7.22222V10.3333C15 13.2665 15 14.7331 14.0888 15.6443C13.1776 16.5556 11.7109 16.5556 8.77778 16.5556H7.22222C4.28904 16.5556 2.82245 16.5556 1.91122 15.6443C1 14.7331 1 13.2665 1 10.3333V7.22222Z" stroke="#344054" strokeWidth="2" />
              <path d="M4.88867 8.77734H11.1109" stroke="#344054" strokeWidth="2" strokeLinecap="round" />
              <path d="M4.88867 5.66699H11.1109" stroke="#344054" strokeWidth="2" strokeLinecap="round" />
              <path d="M4.88867 11.8887H8.77756" stroke="#344054" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <h5 className="mb-0 text-gray-900 text-base font-semibold">{getTitle()}</h5>
        </div>
        <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600">
           <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {/* Name */}
        <div className="flex mb-5">
          <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">Name of Document*</label>
          <div className="w-2/3 pl-2">
            <input
              className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg"
              value={formData.name}
              placeholder="Document Name"
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isViewMode}
            />
            {formError.name && <p className="text-xs text-red-600 mt-1">{formError.name}</p>}
          </div>
        </div>

        {/* ✅ Access Category Radio Buttons with Custom Design */}
        <div className="flex mb-3">
          <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">Session Type*</label>
          <div className="w-2/3 pl-2 flex gap-6 pt-2">
            
            {/* Session Radio */}
            <label className={`flex items-center gap-2 ${isViewMode ? 'pointer-events-none' : 'cursor-pointer'}`}>
              <div className="relative flex items-center justify-center w-4 h-4">
                <input 
                  type="radio" 
                  name="accessCategory" 
                  value="session"
                  checked={accessCategory === 'session'}
                  onChange={() => handleCategoryChange('session')}
                  disabled={isViewMode}
                  className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                    ${accessCategory === 'session' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                    ${isViewMode ? 'cursor-not-allowed' : ''}
                  `}
                />
                {accessCategory === 'session' && (
                  <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                )}
              </div>
              <span className="text-sm text-gray-700"> Company Session</span>
            </label>
            
            {/* Science of Self Radio */}
            <label className={`flex items-center gap-2 ${isViewMode ? 'pointer-events-none' : 'cursor-pointer'}`}>
              <div className="relative flex items-center justify-center w-4 h-4">
                <input
                  type="radio"
                  name="accessCategory"
                  value="sos"
                  checked={accessCategory === 'sos'}
                  onChange={() => handleCategoryChange('sos')}
                  disabled={isViewMode}
                  className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                    ${accessCategory === 'sos' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                    ${isViewMode ? 'cursor-not-allowed' : ''}
                  `}
                />
                {accessCategory === 'sos' && (
                  <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                )}
              </div>
              <span className="text-sm text-gray-700">Science of Self</span>
            </label>

            {/* Sales Training Companies Radio */}
            <label className={`flex items-center gap-2 ${isViewMode ? 'pointer-events-none' : 'cursor-pointer'}`}>
              <div className="relative flex items-center justify-center w-4 h-4">
                <input
                  type="radio"
                  name="accessCategory"
                  value="stc"
                  checked={accessCategory === 'stc'}
                  onChange={() => handleCategoryChange('stc')}
                  disabled={isViewMode}
                  className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                    ${accessCategory === 'stc' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                    ${isViewMode ? 'cursor-not-allowed' : ''}
                  `}
                />
                {accessCategory === 'stc' && (
                  <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                )}
              </div>
              <span className="text-sm text-gray-700">Sales Training Companies</span>
            </label>

          </div>
        </div>

        {/* ✅ Access Groups Checkboxes (Only visible if 'Session' is selected) */}
        {accessCategory === 'session' && (
          <div className="flex mb-5">
            <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">Access For*</label>
            <div className="w-2/3 pl-2">
              <div className="flex gap-6 flex-wrap ">
                {visibleRoles.length > 0 && visibleRoles.map(group => (
                  <label key={group.access_group_id} className={`flex items-center gap-2 ${isViewMode ? 'pointer-events-none' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={formData.access_group_ids.includes(group.access_group_id)}
                      onChange={() => handleAccessGroupChange(group.access_group_id)}
                      disabled={isViewMode}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{group.access_group_name}</span>
                  </label>
                ))}
              </div>
              {formError.access_group_ids && <p className="text-xs text-red-600 mt-1">{formError.access_group_ids}</p>}
            </div>
          </div>
        )}

        {/* Document Upload */}
        <div className="flex mb-5">
          <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">Document</label>
          <div className="w-2/3 pl-2">
            <Upload
              draggable
              showList={true}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="border-2 border-dashed border-gray-300 rounded-lg py-8 px-6 w-full text-center hover:border-gray-400 cursor-pointer"
              onChange={handleFileChange}
              // ✅ Updated to prevent delete in view mode by passing undefined if isViewMode is true
              onFileRemove={isViewMode ? undefined : handleFileRemove}
              multiple
              maxFileSize={25 * 1024 * 1024}
              fileList={formData.documents}
              // ✅ Explicitly set disabled prop
              disabled={isViewMode}
            >
              <div className="flex flex-col items-center justify-center">
                 <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <rect width="40" height="40" rx="20" fill="#F9FAFB" />
                  <path d="M20 16.667v6.666m-3.333-3.333h6.666" stroke="#FF8E1C" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <p className="text-sm font-medium text-orange-500 mb-1">Click to upload file</p>
                <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG allowed</p>
              </div>
            </Upload>
            {formError.documents && <p className="text-xs text-red-600 mt-1">{formError.documents}</p>}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex dailog-footer">
        <button
          className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold"
          onClick={onDialogClose}
          style={{ backgroundColor: '#FFF4E5' }}
        >
          Cancel
        </button>
        {!isViewMode && (
          <BtnPrimary
            disabled={buttonDisabled}
            onClick={handleSubmit}
            className="ml-1.5"
            style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
          >
            {buttonDisabled ? <ThreeDotLoader /> : (
              <>
                {isEditMode ? 'Update Document' : 'Add Document'}
              </>
            )}
          </BtnPrimary>
        )}
      </div>
    </Dialog>
  );
};

export default AddEditSupportedDocument;